import * as LDClient from 'launchdarkly-js-client-sdk';

import {getLaunchDarklyContext} from 'launchdarkly/LaunchDarklyUtil';
import {isNodeTesting} from 'lib/Browser';

/**
 * Initialize the global LaunchDarkly client and attach it to `window.ldClient`.
 *
 * LaunchDarkly clients must be singletons, so we create one here and expose it on `window` so that
 * {@link LaunchDarklyProvider} can pass the same instance into LD's own context provider (rather than
 * instantiating one client per React root).
 *
 * No-op during unit tests (`isNodeTesting`): we don't want to hit the live LD service from tests.
 */
export function initializeLaunchDarklyClient(): void {
  // Don't try to make a LaunchDarkly client if we're in a unit test.
  if (isNodeTesting) {
    return;
  }

  const ldContext = getLaunchDarklyContext();
  // Streaming disabled in order to work around a Safari bug where LD streaming blocks printing.
  // Workaround, if we ever wish to reenable streaming, is to stop streaming immediately before calling
  // window.print and to start streaming again immediately after the call.
  // https://github.com/launchdarkly/js-client-sdk/issues/121
  let ldClient: LDClient.LDClient;
  if (window.LAUNCHDARKLY_CLIENT_URL) {
    ldClient = LDClient.initialize('default', ldContext, {
      baseUrl: window.LAUNCHDARKLY_CLIENT_URL,
      streamUrl: window.LAUNCHDARKLY_CLIENT_URL,
      eventsUrl: window.LAUNCHDARKLY_CLIENT_URL,
      sendEventsOnlyForVariation: true,
    });
  } else {
    ldClient = LDClient.initialize(window.LAUNCHDARKLY_CLIENT_ID, ldContext, {
      streaming: false,
      sendEventsOnlyForVariation: true,
    });
  }

  window.ldClient = {...ldClient, ready: false};
  // Track ready state on the client object itself to avoid unnecessary remounts in modals
  ldClient.on('initialized', () => {
    ldClient.track(window.LAUNCHDARKLY_CLIENT_ID);
  });
}

/**
 * Register a `pagehide` listener that flushes LaunchDarkly analytics on real page unload. Skips the
 * close call when the page may be restored from the back/forward cache (pagehide with persisted=true).
 *
 * No-op if {@link initializeLaunchDarklyClient} was not called (or returned early), since there is no
 * client to close.
 */
export function registerLaunchDarklyPageHideHandler(): void {
  if (window.ldClient == null) {
    return;
  }
  window.addEventListener('pagehide', (e: PageTransitionEvent) => {
    if (!e.persisted) {
      window.ldClient?.close().catch(() => undefined);
    }
  });
}
